/**
 * RestaurantDesktopPresenter — Desktop UI strategy for restaurants.
 * Mirrors ecommerce DesktopPresenter: FlyToCart animation, CartToast,
 * CartSummary, and panel expansion.
 */
import { MenuItemPicker } from './MenuItemPicker.js';
import { CartToast } from '../ecommerce/CartToast.js';
import { CartSummary } from '../ecommerce/CartSummary.js';
import { FlyToCart } from '../ecommerce/FlyToCart.js';
import { generateRestaurantCSS } from './restaurantStyles.js';

export class RestaurantDesktopPresenter {

  constructor(config = {}) {
    this.shadowRoot = config.shadowRoot;
    this.primaryColor = config.primaryColor || '#2563eb';
    this.onItemSelect = config.onItemSelect || (() => {});

    this.flyToCart = new FlyToCart({
      getCartTarget: () => this._getCartTarget(),
      onCartBump: () => this._triggerCartBump(),
      onRipple: () => this._triggerRipple(),
    });

    this.picker = new MenuItemPicker({
      primaryColor: this.primaryColor,
      onItemSelect: (cardEl, item) => this._handleItemSelectWithFly(cardEl, item),
      onAddDisabled: () => this.flyToCart.isAnimating,
      onClose: () => this._handlePickerClose(),
    });

    this.cartToast = new CartToast({
      onUndo: () => {},
      onHide: () => this._restoreWaveform(),
    });

    this.cartSummary = new CartSummary();

    this.container = document.createElement('div');
    this.container.id = 'ttpRestaurantArea';
    this.container.className = 'ttp-restaurant-area';

    const flyTarget = document.createElement('div');
    flyTarget.id = 'ttpDesktopFlyTarget';
    flyTarget.className = 'ttp-desktop-fly-target';
    flyTarget.setAttribute('aria-hidden', 'true');
    this.container.appendChild(flyTarget);

    this.voiceActiveState = null;
    this.isAttached = false;
    this._cartBump = false;
    this._ripple = false;
    this._toastActive = false;
    this._activatedByPresenter = false;
  }

  _getCartTarget() {
    const voiceState = this.voiceActiveState || this.shadowRoot?.getElementById('voiceActiveState');
    if (voiceState) {
      const icon = voiceState.querySelector('.ttp-cart-icon');
      if (icon) {
        const r = icon.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) return icon;
      }
    }
    const root = this.shadowRoot?.getElementById('ttpRestaurantArea') || this.container;
    return root?.querySelector('#ttpDesktopFlyTarget') || null;
  }

  _handleItemSelectWithFly(cardEl, item) {
    if (this.flyToCart.isAnimating) return;
    this.picker.element?.classList.add('ttp-products-animating');
    this.flyToCart.triggerFly(cardEl, item, () => {
      this.picker.element?.classList.remove('ttp-products-animating');
      this._showAddedToast(item);
      this.onItemSelect(item);
    }, { preferClone: false });
  }

  _showAddedToast(item) {
    this._toastActive = true;
    this.hideMenuItems({ shrinkToToastHeight: true });

    const toastEl = this.cartToast.show(item, 'added');
    const callHero = this.shadowRoot?.querySelector('.ttp-call-hero');
    if (callHero) {
      callHero.style.setProperty('display', 'none', 'important');
    }
    const controls = this.shadowRoot?.querySelector('.ttp-controls');
    if (controls) {
      controls.parentNode.insertBefore(toastEl, controls);
    } else if (this.voiceActiveState) {
      this.voiceActiveState.appendChild(toastEl);
    }
  }

  _triggerCartBump() {
    this._cartBump = true;
    setTimeout(() => { this._cartBump = false; }, 450);
  }

  _triggerRipple() {
    if (!this.cartSummary.element) return;
    const existing = this.cartSummary.element.querySelector('.ttp-fly-ripple');
    if (existing) existing.remove();
    const ripple = document.createElement('div');
    ripple.className = 'ttp-fly-ripple';
    this.cartSummary.element.style.position = 'relative';
    this.cartSummary.element.appendChild(ripple);
    setTimeout(() => ripple.remove(), 600);
  }

  showMenuItems(items, title) {
    this._ensureReady();
    this._ensureActiveStateVisible();
    this._switchToCompactMode();
    this._setContainerMode('menu');

    this.picker.hide();

    const pickerEl = this.picker.show(items, title);
    if (pickerEl) {
      this.container.insertBefore(pickerEl, this.container.firstChild);
    }
    this._expandPanelToFit(items.length);
  }

  hideMenuItems(opts = {}) {
    this.picker.hide();
    if (opts.shrinkToToastHeight || this._toastActive) {
      this._setPanelHeightForToast();
    } else {
      this._shrinkPanel();
    }
    this._restoreFullVoiceMode();
    this._setContainerMode('summary');
    if (!opts.shrinkToToastHeight && !this._toastActive) {
      this._restoreIdleStateIfNeeded();
    }
  }

  static TOAST_HEIGHT_PX = 510;

  _setPanelHeightForToast() {
    const apply = () => {
      const panel = this.shadowRoot?.getElementById('text-chat-panel');
      if (!panel) return;
      const height = Math.min(RestaurantDesktopPresenter.TOAST_HEIGHT_PX, Math.floor(window.innerHeight * 0.92));
      panel.classList.add('ttp-panel-expanded');
      panel.style.setProperty('height', `${height}px`, 'important');
    };
    apply();
    requestAnimationFrame(() => requestAnimationFrame(apply));
  }

  _restoreWaveform() {
    const callHero = this.shadowRoot?.querySelector('.ttp-call-hero');
    if (callHero) {
      callHero.style.removeProperty('display');
    }
    this._toastActive = false;
    const panel = this.shadowRoot?.getElementById('text-chat-panel');
    if (panel) {
      panel.classList.remove('ttp-panel-expanded');
      panel.style.removeProperty('height');
    }
  }

  updateOrder(info) {
    this._ensureReady();
    if (info) {
      const count = info.orderItemCount ?? info.itemCount ?? 0;
      const total = info.orderTotal ?? info.total ?? 0;
      this.cartSummary.update(count, total, '$');
    }
  }

  onItemAdded(_product, cartInfo) {
    if (cartInfo) {
      this.cartSummary.update(cartInfo.itemCount, cartInfo.total, cartInfo.currency);
    }
  }

  destroy() {
    if (this.flyToCart) this.flyToCart.destroy();
    this.picker.hide();
    this.cartToast.hide();
    this._shrinkPanel();
    this._restoreFullVoiceMode();
    if (this.voiceActiveState) {
      this.voiceActiveState.classList.remove('ttp-menu-visible');
    }
    if (this.cartSummary.element?.parentNode) {
      this.cartSummary.element.parentNode.removeChild(this.cartSummary.element);
    }
    if (this.container?.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
    this.isAttached = false;
  }

  _ensureReady() {
    if (!this.isAttached) {
      this._injectStyles();
      this._attachToDOM();
    }
  }

  _injectStyles() {
    if (!this.shadowRoot) return;
    if (this.shadowRoot.querySelector('[data-ttp-restaurant]')) return;

    const css = generateRestaurantCSS();
    const style = document.createElement('style');
    style.setAttribute('data-ttp-restaurant', '');
    style.textContent = css;
    this.shadowRoot.appendChild(style);
  }

  _attachToDOM() {
    if (this.isAttached || !this.shadowRoot) return;

    this.voiceActiveState = this.shadowRoot.getElementById('voiceActiveState');
    if (!this.voiceActiveState) {
      console.warn('[RestaurantDesktopPresenter] #voiceActiveState not found');
      return;
    }

    if (this.shadowRoot.getElementById('ttpRestaurantArea')) {
      this.isAttached = true;
      return;
    }

    const compactSection = this.voiceActiveState.querySelector('.compact-voice-section');
    const desktopSection = this.voiceActiveState.querySelector('.desktop-voice-section');
    const insertAfter = compactSection || desktopSection;

    if (insertAfter?.nextSibling) {
      this.voiceActiveState.insertBefore(this.container, insertAfter.nextSibling);
    } else {
      this.voiceActiveState.appendChild(this.container);
    }

    const inputArea = this.voiceActiveState.querySelector('.voice-text-input-area');
    if (inputArea) {
      const cartEl = this.cartSummary.render();
      cartEl.classList.add('ttp-cart-summary-inline');
      inputArea.appendChild(cartEl);
    } else {
      const cartEl = this.cartSummary.render();
      cartEl.classList.add('ttp-cart-summary-bottom');
      this.voiceActiveState.appendChild(cartEl);
    }

    this.isAttached = true;
  }

  _setContainerMode(mode) {
    if (mode === 'menu') {
      this.container.style.flex = '1 1 0';
      this.container.style.minHeight = '0';
      this.container.style.overflowY = 'auto';
      if (this.voiceActiveState) this.voiceActiveState.classList.add('ttp-menu-visible');
    } else {
      this.container.style.flex = '1 1 0';
      this.container.style.minHeight = '0';
      this.container.style.overflowY = '';
      if (this.voiceActiveState) this.voiceActiveState.classList.remove('ttp-menu-visible');
    }
  }

  _expandPanelToFit(_count) {
    if (!this.shadowRoot) return;
    const panel = this.shadowRoot.getElementById('text-chat-panel');
    if (!panel) return;

    panel.classList.add('ttp-panel-expanded');

    const activeState = this.shadowRoot.getElementById('voiceActiveState');
    requestAnimationFrame(() => {
      const compact = activeState?.querySelector('.compact-voice-section');
      const picker = this.container?.querySelector('.ttp-product-picker');
      const transcript = activeState?.querySelector('.desktop-conversation-panel');
      const inputRow = activeState?.querySelector('.voice-text-input-area');

      let needed = 0;
      if (compact) needed += compact.offsetHeight;
      if (picker) needed += picker.scrollHeight + 18;
      if (transcript) needed += Math.min(transcript.offsetHeight, 100);
      if (inputRow) needed += inputRow.offsetHeight;
      needed += 60;

      const maxHeight = Math.min(800, Math.floor(window.innerHeight * 0.9));
      const height = Math.min(Math.max(needed, 380), maxHeight);
      panel.style.height = `${height}px`;
    });
  }

  _shrinkPanel() {
    if (!this.shadowRoot) return;
    const panel = this.shadowRoot.getElementById('text-chat-panel');
    if (panel) {
      panel.classList.remove('ttp-panel-expanded');
      panel.style.height = '';
    }
  }

  _switchToCompactMode() {
    if (!this.shadowRoot) return;
    const originalSection = this.shadowRoot.getElementById('desktopVoiceSection');
    const compactSection = this.shadowRoot.getElementById('compactVoiceSection');
    if (originalSection) originalSection.style.display = 'none';
    if (compactSection) compactSection.style.display = 'flex';
  }

  _restoreFullVoiceMode() {
    if (!this.shadowRoot) return;
    const originalSection = this.shadowRoot.getElementById('desktopVoiceSection');
    const compactSection = this.shadowRoot.getElementById('compactVoiceSection');
    if (originalSection && compactSection) {
      originalSection.style.display = '';
      compactSection.style.display = 'none';
    }
  }

  _ensureActiveStateVisible() {
    if (!this.shadowRoot) return;

    const panel = this.shadowRoot.getElementById('text-chat-panel');
    if (panel && !panel.classList.contains('open')) {
      panel.classList.add('open');
    }

    const landing = this.shadowRoot.getElementById('landingScreen');
    if (landing) landing.classList.remove('active');

    const textIf = this.shadowRoot.getElementById('textInterface');
    if (textIf) textIf.classList.remove('active');

    const vi = this.shadowRoot.getElementById('voiceInterface');
    if (vi && !vi.classList.contains('active')) {
      vi.classList.add('active');
    }

    const activeState = this.shadowRoot.getElementById('voiceActiveState');
    if (!activeState) return;

    const isHidden = activeState.style.display === 'none' ||
      getComputedStyle(activeState).display === 'none';
    if (isHidden) {
      const idleState = this.shadowRoot.getElementById('voiceIdleState');
      if (idleState) idleState.style.display = 'none';
      activeState.style.display = 'flex';
      this._activatedByPresenter = true;
    }
  }

  _restoreIdleStateIfNeeded() {
    if (!this._activatedByPresenter || !this.shadowRoot) return;
    const activeState = this.shadowRoot.getElementById('voiceActiveState');
    const idleState = this.shadowRoot.getElementById('voiceIdleState');
    if (activeState) activeState.style.display = 'none';
    if (idleState) idleState.style.display = 'flex';
    this._activatedByPresenter = false;
  }

  _handlePickerClose() {
    this.hideMenuItems();
  }
}
